import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import EllipsisVerticalIcon from '@heroicons/react/20/solid/EllipsisVerticalIcon';
import { getButtonColorClasses } from '../../buttons/constants';
import { ButtonType } from '../../buttons/ButtonType';
import { useTheme } from '../../../../context/theme/useTheme';
import { IconType } from '../../../../constants';
import { DROPDOWN_TRANSITION } from '../constants';
import { Transition } from '@headlessui/react';

export const PanelDropdown = (props: PanelDropdownProps) => {
  const { panel, buttonClassName, className, isAlert, Icon, buttonType, onDropdownClose, keepOpenOnPanelClick } = props;
  const { isDarkMode } = useTheme();
  const triggerRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Update portal position when trigger button moves or window resizes
  useEffect(() => {
    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();

      setPosition({
        top: rect.bottom + 8, // 8px gap below button (viewport-relative for fixed positioning)
        left: rect.right, // Align right, 224px = w-56 (viewport-relative for fixed positioning)
      });
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Global click handler — close if clicking outside trigger + portal
  useEffect(() => {
    const handleWindowClick = (e: MouseEvent) => {
      if (!isOpen) return;

      const target = e.target as Node;
      const isClickOnTrigger = triggerRef.current?.contains(target);
      const isClickOnPortal = portalRef.current?.contains(target);

      if (!isClickOnTrigger && !isClickOnPortal) {
        setIsOpen(false);
        onDropdownClose?.();
      }
    };

    if (isOpen) {
      window.addEventListener('click', handleWindowClick, true); // Use capture phase
    }

    return () => {
      window.removeEventListener('click', handleWindowClick, true);
    };
  }, [isOpen, onDropdownClose]);

  const DisplayIcon = Icon || EllipsisVerticalIcon;

  return (
    <>
      {/* Trigger Button */}
      <div ref={triggerRef} className={classNames(className, 'relative flex justify-center align-middle')}>
        <button onClick={() => setIsOpen(!isOpen)} className="flex items-center rounded-full">
          <div
            className={classNames(
              buttonClassName,
              getButtonColorClasses({
                buttonType: buttonType ?? (isAlert ? ButtonType.Alert : ButtonType.Link),
                isDarkMode,
              }),
              'rounded-full p-1.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2'
            )}
          >
            <DisplayIcon className="h-6 w-6" />
          </div>
        </button>
      </div>

      {/* Portal Panel */}
      {isOpen &&
        createPortal(
          <Transition show={isOpen} {...DROPDOWN_TRANSITION} afterLeave={() => setIsOpen(false)}>
            <div
              ref={portalRef}
              style={{
                position: 'fixed',
                top: `${position.top}px`,
                left: `${position.left}px`,
                zIndex: 9999,
                transform: 'translateX(-100%)',
              }}
              className="origin-top-right rounded-md shadow-lg"
              onPointerDown={keepOpenOnPanelClick ? (e) => e.stopPropagation() : undefined}
              onClick={keepOpenOnPanelClick ? (e) => e.stopPropagation() : undefined}
            >
              {panel}
            </div>
          </Transition>,
          document.body
        )}
    </>
  );
};

export type PanelDropdownProps = {
  panel: React.ReactNode;
  buttonClassName?: string;
  className?: string;
  isAlert?: boolean;
  Icon?: IconType;
  buttonType?: ButtonType;
  onDropdownClose?: () => void;
  keepOpenOnPanelClick?: boolean;
};
